/**
 * Authentication Event Emitter (Web)
 *
 * Provides a centralized event system for authentication state changes.
 * Allows API interceptors in client.ts to notify AuthContext about auth failures
 * without creating circular dependencies.
 *
 * Ported from apps/mobile/src/lib/authEvents.ts
 */

type EventHandler = () => void;

class SimpleEventEmitter {
  private listeners: Map<string, Set<EventHandler>> = new Map();

  on(event: string, handler: EventHandler): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.listeners.get(event)!.add(handler);
  }

  off(event: string, handler: EventHandler): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(handler);
    }
  }

  emit(event: string): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach((handler) => handler());
    }
  }

  clearAll(): void {
    this.listeners.clear();
  }
}

export const authEventEmitter = new SimpleEventEmitter();

export const AuthEventNames = {
  LOGOUT: 'logout',
} as const;

/**
 * Emit session-expired logout event.
 * Called by client.ts when a token refresh definitively fails.
 * AuthContext handles clearAuth, state reset, and overlay display.
 */
export function emitLogout(): void {
  authEventEmitter.emit(AuthEventNames.LOGOUT);
}

/**
 * Subscribe to logout events. Returns an unsubscribe function.
 */
export function onLogout(handler: EventHandler): () => void {
  authEventEmitter.on(AuthEventNames.LOGOUT, handler);
  return () => authEventEmitter.off(AuthEventNames.LOGOUT, handler);
}

/**
 * Reset all listeners. Only intended for use in tests.
 * @internal
 */
export function __resetAuthEventsForTests(): void {
  authEventEmitter.clearAll();
}
